import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';

import type { PromoRendererProps } from '@ps-int/promozavr/app/PromoRenderer';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import type { RootState } from '../../../../init-store';
import { theme as telemostDarkTheme } from '../../../themes/orb-migrate-dark';
import { theme as telemostLightTheme } from '../../../themes/orb-migrate-light';
import { isDOMAvailable } from '../../helpers';
import { countMetrika } from '../../helpers/metrika';
import { getPromozavrRenderer, getPromozavrRendererSource } from './renderer-source';
import {
    buildPromozavrDebugSnapshot,
    buildPromozavrGetNow,
    buildPromozavrRuntimeViewState,
    normalizePromozavrUrl,
    shouldDispatchPromozavrInit,
    shouldResetPromozavrState,
    syncPromozavrDebugSnapshot,
} from './runtime-helpers';
import { selectPromozavrRuntimeContext } from './selectors';
import { initPromozavr, resetPromozavrState, setPromozavrRendererSource } from './slice';
import { PROMOZAVR_PROJECT } from './types';

const sendMetric: PromoRendererProps['sendMetric'] = (...args) => {
    countMetrika('promozavr', ...args);
};

type PromozavrRuntimeRendererProps = PromoRendererProps & {
    isActive: boolean;
    deactivate: () => void;
};

export type PromozavrRuntimeController = {
    PromoRenderer: ComponentType<PromoRendererProps>;
    promoRendererProps?: PromozavrRuntimeRendererProps;
    renderKey: string;
};

export function usePromozavrRuntimeController(): PromozavrRuntimeController {
    const dispatch = useDispatch();
    const { pathname, search } = useLocation();
    const runtimeContext = useSelector((state: RootState) => selectPromozavrRuntimeContext(state, pathname));
    const [isActive, setIsActive] = useState(true);

    const {
        shouldUsePaas,
        shouldUsePdokPromoRenderer,
        isDebugToolsEnabled,
        isYandexNet,
        isMessenger,
        isMeetingRoomIntegration,
        isTouch,
        appLang,
        currentHost,
        serverTime,
        bodyThemeName,
        status,
        request,
        requestKey,
        selectedPromo,
        translations,
        images,
        isSelected,
        rendererSource,
        lastError,
    } = runtimeContext;

    const viewState = useMemo(
        () =>
            buildPromozavrRuntimeViewState(
                {
                    pathname,
                    search,
                    isYandexNet,
                    shouldUsePaas,
                    shouldUsePdokPromoRenderer,
                    isMessenger,
                    isMeetingRoom: isMeetingRoomIntegration,
                    bodyThemeName,
                    requestKey,
                    selectedPromo,
                    translations,
                    isSelected,
                },
                getPromozavrRendererSource
            ),
        [
            bodyThemeName,
            isMeetingRoomIntegration,
            isMessenger,
            isSelected,
            isYandexNet,
            pathname,
            requestKey,
            search,
            selectedPromo,
            shouldUsePaas,
            shouldUsePdokPromoRenderer,
            translations,
        ]
    );
    const { hasDebugQuery, shouldInit, nextRequestKey, nextRendererSource, shouldRender, isDark } = viewState;

    const deactivate = useCallback(() => {
        setIsActive(false);
    }, []);

    useEffect(() => {
        if (shouldRender) {
            setIsActive(true);
        }
    }, [nextRequestKey, shouldRender]);

    useEffect(() => {
        if (
            shouldResetPromozavrState({
                shouldInit,
                status,
                request,
                requestKey,
                selectedPromo,
                translations,
                images,
                isSelected,
                rendererSource,
                lastError,
                nextRendererSource,
            })
        ) {
            dispatch(resetPromozavrState());
            return;
        }

        if (rendererSource !== nextRendererSource) {
            dispatch(setPromozavrRendererSource(nextRendererSource));
        }
    }, [
        dispatch,
        images,
        isSelected,
        lastError,
        nextRendererSource,
        rendererSource,
        request,
        requestKey,
        selectedPromo,
        shouldInit,
        status,
        translations,
    ]);

    useEffect(() => {
        if (!shouldDispatchPromozavrInit({ shouldInit, requestKey, nextRequestKey })) {
            return;
        }

        dispatch(
            initPromozavr({
                pathname,
                search,
                requestKey: nextRequestKey,
            })
        );
    }, [dispatch, nextRequestKey, pathname, requestKey, search, shouldInit]);

    useEffect(() => {
        if (!isDOMAvailable()) {
            return;
        }

        syncPromozavrDebugSnapshot(
            window,
            buildPromozavrDebugSnapshot({
                pathname,
                shouldInit,
                status,
                requestKey,
                request,
                selectedPromo,
                translations,
                images,
                isSelected,
                rendererSource,
                lastError,
            }),
            hasDebugQuery || isDebugToolsEnabled
        );
    }, [
        hasDebugQuery,
        images,
        isDebugToolsEnabled,
        isSelected,
        lastError,
        pathname,
        rendererSource,
        request,
        requestKey,
        selectedPromo,
        shouldInit,
        status,
        translations,
    ]);

    const PromoRenderer = useMemo(() => getPromozavrRenderer(nextRendererSource), [nextRendererSource]);
    const getNow = useMemo(() => buildPromozavrGetNow(serverTime), [serverTime]);
    const urls = useMemo<PromoRendererProps['urls']>(
        () => ({
            tuning: normalizePromozavrUrl(currentHost),
        }),
        [currentHost]
    );
    const promoRendererProps = useMemo<PromozavrRuntimeRendererProps | undefined>(() => {
        if (!shouldRender || !selectedPromo || !translations) {
            return undefined;
        }

        return {
            currentPromo: selectedPromo,
            translations,
            imageStore: images,
            sendMetric,
            theme: isDark ? telemostDarkTheme : telemostLightTheme,
            darkTheme: telemostDarkTheme,
            lightTheme: telemostLightTheme,
            isDark,
            getNow,
            lang: appLang as PromoRendererProps['lang'],
            project: PROMOZAVR_PROJECT as PromoRendererProps['project'],
            isTouch,
            urls,
            isActive,
            deactivate,
            onClose: deactivate,
        };
    }, [
        appLang,
        deactivate,
        getNow,
        images,
        isActive,
        isDark,
        isTouch,
        selectedPromo,
        shouldRender,
        translations,
        urls,
    ]);

    return {
        PromoRenderer,
        promoRendererProps,
        renderKey: nextRequestKey,
    };
}
